import { useState, useCallback, useMemo } from "react";
import {
  computeInputValue,
  computeFilteredOptions,
  computeFilteredData,
} from "./useSelectState.helpers";
import type {
  SelectOption,
  DisplayNameResolver,
  SelectSearchableParams,
  SelectNonSearchableParams,
  UseSelectOnSelectParams,
  UseSelectCallbacksParams,
  UseSelectFilteredParams,
} from "./useSelectState.types";

/**
 * Commits a choice in searchable single-select mode: syncs the query text to
 * the option's display name, stores the value, notifies the consumer, and
 * closes the dropdown. No-op when no option value is provided.
 *
 * @param params Bundled setters, the chosen value, and change callback.
 */
export function selectSearchable(params: SelectSearchableParams): void {
  const {
    optionValue, getDisplayNameForValue, displayNameRef, setQuery,
    setSelectedValue, onChange, name, setIsFocused, setIsOpen, setSearchTerm,
  } = params;
  if (!optionValue) return;
  const displayName = getDisplayNameForValue(optionValue);
  displayNameRef.current = displayName;
  setQuery(displayName);
  setSelectedValue(optionValue);
  onChange(optionValue, name);
  setIsFocused(false);
  setIsOpen(false);
  setSearchTerm('');
}

/**
 * Commits a choice in non-searchable single-select mode. Toggles the value off
 * when deselection is allowed and the same option is re-picked; otherwise
 * selects it. Always closes the dropdown afterwards.
 *
 * @param params Bundled current value, deselect flag, setters, and callback.
 */
export function selectNonSearchable(params: SelectNonSearchableParams): void {
  const { optionValue, selectedValue, allowDeselect, onChange, name, setSelectedValue, close } = params;
  if (allowDeselect && selectedValue === optionValue) {
    onChange(null, name);
    setSelectedValue('');
  } else {
    onChange(optionValue, name);
    setSelectedValue(optionValue ?? '');
  }
  close();
}

/**
 * Initializes the core local state slots (selected value, focus, query,
 * active option index, open flag, search term) for a select input.
 *
 * @param value Initial controlled value, if any.
 * @param searchable Whether the select supports typed search.
 * @param getDisplayNameForValue Resolves the initial query display name.
 * @returns The state values paired with their setters.
 */
export function useSelectCoreState(
  value: string | null | undefined,
  searchable: boolean,
  getDisplayNameForValue: DisplayNameResolver,
) {
  const [selectedValue, setSelectedValue] = useState(value ?? '');
  const [isFocused, setIsFocused] = useState(false);
  const [query, setQuery] = useState(() => (value ? getDisplayNameForValue(value) : ''));
  const [activeOptionIndex, setActiveOptionIndex] = useState(searchable ? 0 : -1);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  return {
    selectedValue, setSelectedValue, isFocused, setIsFocused,
    query, setQuery, activeOptionIndex, setActiveOptionIndex,
    isOpen, setIsOpen, searchTerm, setSearchTerm,
  };
}

/**
 * Derives the visible input text, the matched option object, and its label
 * from the current select state.
 *
 * @returns `inputValue`, `selectedOption`, and `displayValue`.
 */
export function useSelectDerived(
  normalizedData: SelectOption[],
  value: string | null | undefined,
  selectedValue: string,
  query: string,
  isFocused: boolean,
  getDisplayNameForValue: DisplayNameResolver,
) {
  const inputValue = useMemo(() => {
    const expected = getDisplayNameForValue(selectedValue);
    return computeInputValue(selectedValue, query, isFocused, expected);
  }, [selectedValue, query, isFocused, getDisplayNameForValue]);

  const selectedOption = useMemo(
    () => normalizedData.find((option) => option.value === (value ?? selectedValue)),
    [normalizedData, value, selectedValue],
  );

  const displayValue = selectedOption?.label || '';
  return { inputValue, selectedOption, displayValue };
}

/**
 * Computes the memoized filtered option and data lists for the dropdown,
 * respecting search term, trigger mode, and the max-results cap.
 *
 * @param params Filtering inputs (mode flags, query, source lists, limits).
 * @returns `filteredOptions` (strings) and `filteredData` (option objects).
 */
export function useSelectFiltered(params: UseSelectFilteredParams) {
  const {
    searchable, searchTerm, useTriggerMode, query,
    options, normalizedData, filterFunction, maxResults,
  } = params;
  const args = { searchable, searchTerm, useTriggerMode, query, options, normalizedData, filterFunction, maxResults };
  const filteredOptions = useMemo(
    () => computeFilteredOptions(args),
    [query, options, filterFunction, maxResults, searchable, searchTerm, normalizedData, useTriggerMode],
  );
  const filteredData = useMemo(
    () => computeFilteredData(args),
    [query, normalizedData, options, filterFunction, maxResults, searchable, searchTerm, useTriggerMode],
  );
  return { filteredOptions, filteredData };
}

/**
 * Builds the stable interaction callbacks for the select: closing the
 * dropdown, reacting to query/search-term changes, clearing the value, and
 * toggling the open state.
 *
 * @param params Bundled state setters plus search/change configuration.
 * @returns `close`, `handleQueryChange`, `handleSearchTermChange`,
 *   `handleClear`, and `toggleOpen`.
 */
export function useSelectCallbacks(params: UseSelectCallbacksParams) {
  const {
    setQuery, setIsFocused, setIsOpen, setSearchTerm,
    setActiveOptionIndex, setSelectedValue, searchable, onChange, name,
  } = params;
  const close = useCallback(() => {
    setIsOpen(false);
    setSearchTerm('');
    setActiveOptionIndex(searchable ? 0 : -1);
  }, [searchable]);
  const handleQueryChange = useCallback((newQuery: string) => {
    setQuery(newQuery);
    if (newQuery) setIsFocused(true);
  }, []);
  const handleSearchTermChange = useCallback((term: string) => { setSearchTerm(term); }, []);
  const handleClear = useCallback(() => {
    onChange(null, name);
    setSelectedValue('');
  }, [onChange, name]);
  const toggleOpen = useCallback(() => { setIsOpen((prev) => !prev); }, []);
  return { close, handleQueryChange, handleSearchTermChange, handleClear, toggleOpen };
}

/**
 * Produces the `onSelect` handler that commits a chosen option. Delegates to
 * the multiple-toggle path, the searchable single-select path, or the
 * non-searchable single-select path depending on configuration.
 *
 * @param params Mode flags, setters, and callbacks for the three paths.
 * @returns A memoized `(optionValue: string | null) => void` handler.
 */
export function useSelectOnSelect(params: UseSelectOnSelectParams) {
  const {
    multiple, searchable, allowDeselect, toggleValue, onChange, name,
    getDisplayNameForValue, displayNameRef, setQuery, setSelectedValue,
    setIsFocused, setIsOpen, setSearchTerm, selectedValue, close,
  } = params;
  return useCallback(
    (optionValue: string | null) => {
      if (multiple) {
        if (optionValue) toggleValue(optionValue);
        return;
      }
      if (searchable) {
        selectSearchable({
          optionValue, getDisplayNameForValue, displayNameRef, setQuery,
          setSelectedValue, onChange, name, setIsFocused, setIsOpen, setSearchTerm,
        });
        return;
      }
      selectNonSearchable({ optionValue, selectedValue, allowDeselect, onChange, name, setSelectedValue, close });
    },
    [multiple, toggleValue, onChange, name, getDisplayNameForValue, searchable, allowDeselect, selectedValue, close],
  );
}
